<template>
  <div class="checkout-builder-container">
    <draggable
      handle=".handle"
      class="draggable"
      :class="{ active: canAddElement, editing }"
      :group="{ name: 'components', put: canAddElement, pull: false }"
      @add="onAddElement"
    >
      <element-preview
        v-for="component in _components"
        class="preview-component"
        :key="component.id"
        :component="component"
        @edit="editComponent(component)"
        @remove="removeComponent(component)"
      />

      <template #footer v-if="editing">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            fill="none"
            stroke-width="3"
            stroke-linecap="round"
            stroke-dasharray="10 6"
            vector-effect="non-scaling-stroke"
            d="M0 0 L0 100 L100 100 L100 0 Z"
          />
        </svg>
        <div class="draggable-message d-none d-lg-flex">
          <span>
            {{ draggableMessage }}
          </span>
        </div>

        <button
          v-if="!_components.length"
          class="add-element-button d-lg-none"
          @click="elementsModal = true"
        >
          <i class="ph ph-plus mr-3" />
          Adicionar elementos
        </button>
      </template>
    </draggable>

    <div
      class="d-lg-none mt-3"
      v-if="editing && _components.length && position != 'timer'"
    >
      <bp-button
        block
        size="sm"
        label="Elementos"
        variant="outlined-1"
        icon="ph ph-plus"
        @click="elementsModal = true"
      />
    </div>

    <responsive-modal v-model="elementsModal" title="Adicionar elemento">
      <elements-modal
        :allow="allow"
        @add-element="openModal"
        @close="elementsModal = false"
      />
    </responsive-modal>

    <responsive-modal v-model="modal" :title="modalTitle">
      <component
        :data="activeComponent"
        :position="position"
        :is="modalComponent"
        @close="modal = false"
        @save="saveComponent"
      />
    </responsive-modal>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { mapState } from "vuex";

import ElementsModal from "../elements/ElementsModal.vue";
import ImageModal from "../elements/ImageModal.vue";
import TextModal from "../elements/TextModal.vue";
import TimerModal from "../elements/TimerModal.vue";
import VideoModal from "../elements/VideoModal.vue";

import ElementPreview from "./preview/ElementPreview.vue";

export default {
  components: {
    draggable,
    ElementsModal,
    ElementPreview
  },
  props: {},
  data() {
    return {
      position: "",
      allow: [],

      modal: false,
      modalComponent: null,
      elementsModal: false,
      activeComponent: {}
    };
  },
  computed: {
    ...mapState("checkoutBuilder", [
      "element",
      "components",
      "device",
      "editing"
    ]),
    _components() {
      return this.components
        .filter(
          c =>
            !c.deleted &&
            c.position == this.position &&
            c.config?.display &&
            c.config.display.includes(this.device)
        )
        .sort((a, b) => a.order - b.order);
    },
    canAddElement() {
      if (!this.editing) return false;
      return (
        (window.innerWidth < 992 && !this._components.length) ||
        (this.element && this.allow.includes(this.element))
      );
    },
    draggableMessage() {
      return this._components.length
        ? "Arraste e solte para adicionar mais elementos"
        : "Arraste e solte os elementos aqui";
    },
    modalTitle() {
      if (!this.modalComponent) return "";

      const titles = new Map([
        [ImageModal, "imagem"],
        [VideoModal, "vídeo"],
        [TextModal, "texto"],
        [TimerModal, "cronômetro"]
      ]);

      const baseTitle = titles.get(this.modalComponent) || "Elemento";
      const isEdit = !!this.activeComponent?.id;

      return isEdit ? `Editar ${baseTitle}` : `Inserir ${baseTitle}`;
    }
  },
  methods: {
    getPreviewComponent(component) {
      if (!component) return;
      const previews = {
        image: ImagePreview,
        video: VideoPreview,
        text: TextPreview,
        timer: TimerPreview
      };
      return previews[component.type];
    },

    onAddElement(e) {
      e.item.remove();
      this.openModal(this.element);
    },

    openModal(type) {
      const forms = {
        image: ImageModal,
        video: VideoModal,
        text: TextModal,
        timer: TimerModal
      };
      this.modalComponent = forms[type];
      this.modal = true;
    },

    editComponent(component) {
      this.activeComponent = component;
      this.openModal(component.type);
    },
    saveComponent(component) {
      const components = [...this.components];

      component.position = this.position;
      if (component.id) {
        const index = this.components.findIndex(c => c.id == component.id);
        components[index] = { ...this.activeComponent, ...component };
      } else {
        component.id = 1620000000000 - Date.now();
        component.order = this.components.length + 1;
        components.push(component);
      }

      this.$store.dispatch("checkoutBuilder/saveSnapshot");
      this.$store.commit("checkoutBuilder/setComponents", components);

      this.modal = false;
    },
    removeComponent(component) {
      const components = this.components
        .sort((a, b) => a.order - b.order)
        .map((c, i) =>
          c.id == component.id
            ? { ...component, deleted: true }
            : { ...c, order: i }
        );

      this.$store.dispatch("checkoutBuilder/saveSnapshot");
      this.$store.commit("checkoutBuilder/setComponents", components);
    }
  },
  watch: {
    modal(v) {
      if (!v) this.activeComponent = {};
    }
  }
};
</script>

<style lang="scss" scoped>
.checkout-builder-container {
  .draggable {
    position: relative;
    height: 100%;

    &.editing {
      &:not(:has(.preview-component)) {
        min-height: 84px;
      }
      @media screen and (max-width: 991px) {
        &:has(.preview-component) {
          min-height: min-content;
          height: min-content;
        }
      }

      .preview-component {
        min-height: 56px;
      }
    }

    svg,
    .draggable-message {
      inset: 0;
      opacity: 0;
      z-index: -1;
      position: absolute;
      transition: 0.2s opacity;
    }
    svg {
      backdrop-filter: blur(6px);
      background-color: #19192205;
      path {
        transition: 0.2s all;
        stroke: #c0c0c0;
      }
    }
    .draggable-message {
      padding: 12px;
      display: flex;
      text-align: center;
      color: #c0c0c0;
      align-items: center;
      justify-content: center;
    }

    &:not(:has(.preview-component)) {
      svg,
      .draggable-message {
        opacity: 1;
        z-index: 1;
      }
    }

    &.active {
      svg,
      .draggable-message {
        opacity: 1;
        z-index: 1;
        color: var(--blue-secondary);
      }
      svg {
        background-color: #ffffffcc;
      }
      path {
        stroke: var(--blue-secondary);
      }
      ::v-deep iframe {
        display: none;
      }
    }

    &:has(.draggable-element) {
      svg,
      .draggable-message {
        opacity: 0;
      }
      ::v-deep .draggable-element {
        display: flex;
        border-radius: 4px;
        align-items: center;
        flex-direction: column;
        justify-content: center;
        background-color: $neutral-100;
        border: 1px solid $neutral-400;
      }
      ::v-deep iframe {
        display: block;
      }
    }

    .add-element-button {
      inset: 1px;
      z-index: 2;
      border: none;
      color: var(--blue-secondary);
      position: absolute;
      text-align: center;
      background-color: transparent;
    }
  }

  .mobile-add-button {
    width: 100%;
    border: none;
    display: flex;
    position: relative;
    align-items: center;
    justify-content: center;
    background-color: transparent;
  }
}
</style>
